import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Package, Search } from 'lucide-react'
import { useAmazonProducts } from '@/hooks/useAmazonProducts'
import { useProcessQueue } from '@/hooks/useAmazonAudit'
import { Pagination } from '@/components/v2/ui'
import { AmazonProductCard } from './AmazonProductCard'
import { CampaignCreator } from './CampaignCreator'
import { AuditImageSettings } from './AuditImageSettings'

const PAGE_SIZE = 24

export function AmazonProductOverview() {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const { data, isLoading } = useAmazonProducts({
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    search: search || undefined,
  })
  useProcessQueue()

  const total = data?.total ?? 0
  const totalPages = total > 0 ? Math.ceil(total / PAGE_SIZE) : 0

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 shadow-lg shadow-orange-500/30">
            <Package className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white tracking-tight">
              {t('amazonListing.title')}
            </h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
              {t('amazonListing.subtitle')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <AuditImageSettings />
          <CampaignCreator />
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder={t('amazonListing.searchProducts')}
          className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
        />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-40 bg-neutral-100 dark:bg-neutral-800 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : data?.items?.length ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.items.map((item) => (
              <AmazonProductCard key={item.id} product={item} />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-800">
              <div className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">
                {t('amazonListing.paginationInfo', { current: page, total: totalPages, count: total })}
              </div>
              <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-16">
          <Package className="h-12 w-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
          <h3 className="text-lg font-semibold text-neutral-700 dark:text-neutral-300">
            {t('amazonListing.noProducts')}
          </h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 max-w-md mx-auto">
            {t('amazonListing.noProductsDesc')}
          </p>
        </div>
      )}

    </div>
  )
}
